import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cookieManager } from '../../utils/cookieManager';

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentProps {
  onAccept?: (preferences: CookiePreferences) => void;
  onReject?: () => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({ onAccept, onReject }) => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Necessary cookies always enabled
    functional: false,
    analytics: false,
    marketing: false,
  });
  const [browserInfo, setBrowserInfo] = useState<{
    isKnownBrowser: boolean;
    fingerprint: string;
    storedCount: number;
  } | null>(null);

  useEffect(() => {
    // Check if cookie preferences have been set
    const hasConsent = cookieManager.hasConsent();
    const browserInfo = cookieManager.getBrowserInfo();
    
    setBrowserInfo(browserInfo);
    
    if (!hasConsent) {
      setShowBanner(true);
      console.log('Cookie consent required:', {
        isKnownBrowser: browserInfo.isKnownBrowser,
        fingerprint: browserInfo.fingerprint,
        storedCount: browserInfo.storedCount,
      });
    } else {
      console.log('Cookie consent already given for this browser');
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    savePreferences(allAccepted);
    setShowBanner(false);
    onAccept?.(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    setPreferences(onlyNecessary);
    savePreferences(onlyNecessary);
    setShowBanner(false);
    onReject?.();
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
    setShowBanner(false);
    setShowPreferences(false);
    onAccept?.(preferences);
  };

  const savePreferences = (prefs: CookiePreferences) => {
    // Use cookieManager to save preferences, it automatically handles browser fingerprinting
    cookieManager.setPreferences(prefs);
  };

  const handlePreferenceChange = (category: keyof CookiePreferences, value: boolean) => {
    if (category === 'necessary') return; // Necessary cookies cannot be disabled
    setPreferences(prev => ({
      ...prev,
      [category]: value,
    }));
  };

  const cookieCategories = [
    {
      key: 'necessary' as keyof CookiePreferences,
      title: t('cookieConsent.categories.necessary.title'),
      description: t('cookieConsent.categories.necessary.description'),
      required: true,
    },
    {
      key: 'functional' as keyof CookiePreferences,
      title: t('cookieConsent.categories.functional.title'),
      description: t('cookieConsent.categories.functional.description'),
      required: false,
    },
    {
      key: 'analytics' as keyof CookiePreferences,
      title: t('cookieConsent.categories.analytics.title'),
      description: t('cookieConsent.categories.analytics.description'),
      required: false,
    },
    {
      key: 'marketing' as keyof CookiePreferences,
      title: t('cookieConsent.categories.marketing.title'),
      description: t('cookieConsent.categories.marketing.description'),
      required: false,
    },
  ];

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Icon and Text */}
            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="fas fa-cookie-bite text-teal-600 text-xs sm:text-sm"></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 whitespace-nowrap">{t('cookieConsent.title')}</h3>
                  <button
                    onClick={() => setShowPreferences(true)}
                    className="text-teal-600 hover:text-teal-700 text-xs font-medium underline flex-shrink-0"
                  >
                    {t('cookieConsent.learnMore')}
                  </button>
                </div>
                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed line-clamp-2 hidden sm:block">
                  {t('cookieConsent.description')}
                </p>
              </div>
            </div>
            {/* Buttons */}
            <div className="flex gap-2 sm:gap-3 flex-shrink-0">
              <button
                onClick={handleRejectAll}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap"
              >
                {t('cookieConsent.rejectAll')}
              </button>
              <button
                onClick={() => setShowPreferences(true)}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm border border-teal-300 text-teal-600 rounded-lg hover:bg-teal-50 transition-colors font-medium whitespace-nowrap hidden sm:inline-block"
              >
                {t('cookieConsent.preferences')}
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-4 sm:px-6 py-1.5 sm:py-2 text-xs sm:text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap"
              >
                {t('cookieConsent.acceptAll')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                    <i className="fas fa-cookie-bite text-teal-600"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{t('cookieConsent.preferencesTitle')}</h2>
                </div>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                >
                  <i className="fas fa-times text-gray-600"></i>
                </button>
              </div>

              {/* Description */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-blue-800 text-sm">
                  {t('cookieConsent.preferencesDescription')}
                </p>
              </div>

              {/* Cookie Categories */}
              <div className="space-y-4 mb-6">
                {cookieCategories.map((category) => (
                  <div key={category.key} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{category.title}</h3>
                        {category.required && (
                          <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                            {t('cookieConsent.required')}
                          </span>
                        )}
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences[category.key]}
                          onChange={(e) => handlePreferenceChange(category.key, e.target.checked)}
                          disabled={category.required}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                      </label>
                    </div>
                    <p className="text-gray-600 text-sm">{category.description}</p>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleRejectAll}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  {t('cookieConsent.rejectAll')}
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                >
                  {t('cookieConsent.savePreferences')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
